import { useState, useEffect } from "react";
import { Upload, Trash2, Star } from "lucide-react";
import { FiEye, FiPlusCircle } from "react-icons/fi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useHouseDesign } from "./useHook";
import Swal from "sweetalert2";
import SimilarDesignModal from "../../Components/Modals/SimilarHouseDesign/SimilarDesign";
import AddPopularDesign from "../../Components/Modals/PopulaHouseDesign/PopularHouseDesing";
import { useNavigate } from "react-router-dom";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";

const HouseDesign = () => {
  const navigate = useNavigate();
  const {
    getHouseDesign,
    addHouseDesign,
    updateHouseDesign,
    deleteHouseDesign,
    searchHouseDesign,
  } = useHouseDesign();

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [houseDesignData, setHouseDesignData] = useState([]);
  const [isSimilarDesngModalOpen, setIsSimilarDesngModalOpen] = useState(false);
  const [showPopularDesignModal, setShowPopularDesignModal] = useState(false);
  const [imageError, setImageError] = useState("");
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    plot_dimension: "",
    covered_area: "",
    floors: 0,
    bed_rooms: 0,
    bath_rooms: 0,
    associated_house: "",
    customUrl: "",
    description: "",
    servant_quarters: false,
    terrace: false,
    car_parking: false,
    images: [],
    imagePreviews: [],
    primaryImageIndex: 0, // Add primary image index
    // price: 0,
    name: "",
  });
  useEffect(() => {
    loadHouseDesigns(0, limit);
  }, [offset]);

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
    loadHouseDesigns(newOffset, limit);
  };
  const closeSimilarDesngModal = () => {
    setIsSimilarDesngModalOpen(false);
  };
  const loadHouseDesigns = async (currentOffset = offset, currentLimit = limit, currentSearchFilter = searchFilter) => {
    setIsLoading(true);
    try {
      await getHouseDesign(setHouseDesignData, setTotal, currentOffset, currentLimit, currentSearchFilter);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load house designs. Please refresh the page.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setOffset(0);
      const response = await searchHouseDesign(searchFilter, 0, limit);
      setHouseDesignData(response.data);
      const totalRecords = response.pagination?.totalRecords || response.data.length;
      setTotal(totalRecords);
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    setOffset(0);
    loadHouseDesigns(0, limit, "");
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };
  const handleQuillChange = (value) => {
    setFormData({
      ...formData,
      description: value,
    });
  };
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newValidImages = [];
    const newImagePreviews = [];

    const checkImageResolution = (file) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width === 1600 && img.height === 900) {
            resolve(true);
          } else {
            resolve(false);
          }
        };
        img.src = URL.createObjectURL(file);
      });
    };

    Promise.all(files.map((file) => checkImageResolution(file))).then(
      (results) => {
        results.forEach((isValid, index) => {
          if (isValid) {
            newValidImages.push(files[index]);
            newImagePreviews.push(URL.createObjectURL(files[index]));
          } else {
            alert(
              `Image "${files[index].name}" does not have the required resolution of 1600*900. Please upload the correct resolution.`
            );
          }
        });
        setFormData((prev) => {
          const currentImageCount = prev.images ? prev.images.length : 0;
          const newPrimaryIndex = currentImageCount === 0 ? 0 : prev.primaryImageIndex;

          return {
            ...prev,
            images: [...(prev.images || []), ...newValidImages],
            imagePreviews: [...(prev.imagePreviews || []), ...newImagePreviews],
            primaryImageIndex: newPrimaryIndex,
          };
        });
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.customUrl || formData.customUrl.trim() === "") {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL is required. Please enter a custom URL.",
      });
      return;
    }

    if (formData.customUrl.trim().startsWith('/')) {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL cannot start with a forward slash (/). Please enter a valid URL.",
      });
      return;
    }

    if (!formData.images || formData.images.length === 0) {
      setImageError("At least one image is required");
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please upload at least one image before submitting.",
      });
      return;
    }

    try {
      const apiData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key !== "image" && key !== "imagePreviews" && key !== "primaryImageIndex") {
          apiData.append(key, formData[key]);
        }
      });

      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          apiData.append(`image`, image);
          // Add isPrimary flag for each image
          const isPrimary = index === formData.primaryImageIndex;
          apiData.append(`isPrimary`, isPrimary);
        });
      }

      let response;
      if (isEditing) {
        response = await updateHouseDesign(editId, apiData);
      } else {
        response = await addHouseDesign(apiData);
      }

      await loadHouseDesigns(offset, limit);

      setShowModal(false);
      setIsEditing(false);
      setEditId(null);
      resetForm();

      Swal.fire({
        icon: "success",
        title: isEditing ? "House Design Updated" : "House Design Added",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Failed to submit form:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save house design. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const resetForm = () => {
    if (formData.imagePreviews) {
      formData.imagePreviews.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    }

    setFormData({
      title: "",
      plot_dimension: "",
      covered_area: "",
      floors: 0,
      bed_rooms: 0,
      bath_rooms: 0,
      description: "",
      customUrl: "",
      servant_quarters: false,
      terrace: false,
      car_parking: false,
      // price: 0,
      name: "",
      images: [],
      imagePreviews: [],
      primaryImageIndex: 0,
    });
  };
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await deleteHouseDesign(id);
        await loadHouseDesigns(offset, limit);
        Swal.fire("Deleted!", "House design deleted successfully.", "success");
      } catch (error) {
        console.error("Failed to delete house design:", error);
        Swal.fire(
          "Error!",
          "Failed to delete house design. Please try again.",
          "error"
        );
      }
    }
  };
  const removeImage = (index) => {
    const updatedImages = [...formData.images];
    const updatedPreviews = [...(formData.imagePreviews || [])];

    if (updatedPreviews[index]) {
      URL.revokeObjectURL(updatedPreviews[index]);
    }

    updatedImages.splice(index, 1);
    updatedPreviews.splice(index, 1);

    // Adjust primary image index if the removed image was primary or before it
    let newPrimaryIndex = formData.primaryImageIndex;
    if (index <= formData.primaryImageIndex) {
      newPrimaryIndex = Math.max(0, formData.primaryImageIndex - 1);
    }

    setFormData({
      ...formData,
      images: updatedImages,
      imagePreviews: updatedPreviews,
      primaryImageIndex: newPrimaryIndex,
    });
    if (updatedImages.length === 0) {
      setImageError("At least one image is required");
    }
  };

  const setPrimaryImage = (index) => {
    setFormData({
      ...formData,
      primaryImageIndex: index,
    });
  };
  const handlePageView = (id) => {
    navigate(`/house-design/${id}`);
  };
  useEffect(() => {
    return () => {
      if (formData.imagePreviews) {
        formData.imagePreviews.forEach((url) => {
          URL.revokeObjectURL(url);
        });
      }
    };
  }, [formData.imagePreviews]);
  const handleFeatureChange = (featureName) => {
    setFormData({
      ...formData,
      [featureName]: !formData[featureName],
    });
  };
  const handleAddSimilarDesign = (id) => {
    setIsSimilarDesngModalOpen(id);
  };

  return (
    <div className="bg-gray-100">
      {/* Content */}
      <div className="p-2">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">House Design</h3>
          <div className="space-x-2">
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded-md"
              onClick={() => {
                resetForm();
                setIsEditing(false);
                setShowModal(true);
              }}
            >
              Add House Design
            </button>
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded-md"
              onClick={() => {
                setShowPopularDesignModal(true);
              }}
            >
              Add Slide Show
            </button>
          </div>
        </div>

        {/* Search Filter */}
        <div className="bg-white p-4 rounded-md shadow-md mb-6 flex gap-4">
          <input
            type="text"
            className="w-[40%] flex-grow px-4 py-2 border rounded-lg"
            placeholder="Search house designs"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
          <button
            className="w-full sm:w-[45%] md:w-60 bg-blue-500 text-white px-4 py-2 rounded-md"
            onClick={handleSearch}
          >
            Search
          </button>
          <button
            className="w-full sm:w-[45%] md:w-60 bg-primary-dark text-white px-4 py-2 rounded-md"
            onClick={handleReset}
          >
            Reset
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-md shadow-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-primary-dark to-primary-light text-white">
                <th className="py-3 px-4 text-left">Sr. No.</th>
                <th className="py-3 px-4 text-left">Title</th>
                <th className="py-3 px-4 text-left">Plot Dimension</th>
                <th className="py-3 px-4 text-left">Covered Area</th>
                <th className="py-3 px-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {houseDesignData.length > 0 ? (
                houseDesignData.map((design, index) => (
                  <tr key={design.id} className="border-b">
                    <td className="py-3 px-4">{offset + index + 1}</td>
                    <td className="py-3 px-4">{design.title || design.name}</td>
                    <td className="py-3 px-4">{design.plot_dimension}</td>
                    <td className="py-3 px-4">{design.covered_area}</td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        {/* <button
                          className="bg-blue-500 text-white p-2 rounded-md"
                          onClick={() => handleEdit(design.id)}
                        >
                          <Edit2 size={16} />
                        </button> */}
                        <button
                          className="bg-red-500 text-white p-2 rounded-md"
                          onClick={() => handleDelete(design.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                        <button
                          className="bg-blue-500 text-white p-2 rounded-md flex items-center gap-1"
                          onClick={() => handlePageView(design.id)}
                        >
                          <FiEye size={16} />
                        </button>
                        <button
                          className="bg-green-500 text-white p-2 rounded-md flex items-center gap-1"
                          onClick={() => handleAddSimilarDesign(design.id)}
                        >
                          <FiPlusCircle size={16} />
                          Similar Design
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="py-4 px-4 text-center text-gray-500 font-bold"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading house designs...
                      </div>
                    ) : "No house designs found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {houseDesignData.length > 0 && total > limit && (
          <div className="mt-4">
            <GlobalPagination
              offset={offset}
              limit={limit}
              total={total}
              onPageChange={handlePageChange}
              className="bg-white"
            />
          </div>
        )}
      </div>

      {/* Add/Edit House Design Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-1/2 relative max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                {isEditing ? "Edit House Design" : "Add House Design"}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plot Dimension
                  </label>
                  <input
                    type="text"
                    name="plot_dimension"
                    value={formData.plot_dimension}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Covered Area
                  </label>
                  <input
                    type="text"
                    name="covered_area"
                    value={formData.covered_area}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Floors
                  </label>
                  <input
                    type="number"
                    name="floors"
                    value={formData.floors}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bedrooms
                  </label>
                  <input
                    type="number"
                    name="bed_rooms"
                    value={formData.bed_rooms}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bathrooms
                  </label>
                  <input
                    type="number"
                    name="bath_rooms"
                    value={formData.bath_rooms}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Associate to category
                  </label>
                  <select
                    name="associated_house"
                    value={formData.associated_house}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  >
                    <option value="">Select category</option>
                    <option value="3 Marla">3 Marla</option>
                    <option value="5 Marla">5 Marla</option>
                    <option value="8 Marla">8 Marla</option>
                    <option value="10 Marla">10 Marla</option>
                    <option value="1 kanal">1 Kanal</option>
                    <option value="2 kanal">2 Kanal</option>
                    <option value="others">Others</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Url <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="customUrl"
                    value={formData.customUrl}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <div className="border rounded-md">
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={handleQuillChange}
                    className="h-40"
                  />
                </div>
              </div>

              {/* Feature Checkboxes */}
              <div className="mb-4">
                <h3 className="mt-12 text-sm font-medium text-gray-700 mb-2">
                  Features
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div
                    className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleFeatureChange("servant_quarters")}
                  >
                    <input
                      type="checkbox"
                      id="servant_quarters"
                      name="servant_quarters"
                      checked={formData.servant_quarters}
                      onChange={() => handleFeatureChange("servant_quarters")}
                      className="mr-2 h-4 w-4 text-blue-500 focus:ring-blue-400"
                    />
                    <label
                      htmlFor="servant_quarters"
                      className="cursor-pointer select-none"
                      onClick={(e) => e.preventDefault()}
                    >
                      Servant Quarters
                    </label>
                  </div>
                  <div
                    className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleFeatureChange("terrace")}
                  >
                    <input
                      type="checkbox"
                      id="terrace"
                      name="terrace"
                      checked={formData.terrace}
                      onChange={() => handleFeatureChange("terrace")}
                      className="mr-2 h-4 w-4 text-blue-500 focus:ring-blue-400"
                    />
                    <label
                      htmlFor="terrace"
                      className="cursor-pointer select-none"
                      onClick={(e) => e.preventDefault()}
                    >
                      Terrace
                    </label>
                  </div>
                  <div
                    className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleFeatureChange("car_parking")}
                  >
                    <input
                      type="checkbox"
                      id="car_parking"
                      name="car_parking"
                      checked={formData.car_parking}
                      onChange={() => handleFeatureChange("car_parking")}
                      className="mr-2 h-4 w-4 text-blue-500 focus:ring-blue-400"
                    />
                    <label
                      htmlFor="car_parking"
                      className="cursor-pointer select-none"
                      onClick={(e) => e.preventDefault()}
                    >
                      Car Parking
                    </label>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Images <span className="text-lg text-red-500">*</span>
                </label>
                <div className="border border-dashed border-gray-300 p-4 rounded-md relative group">
                  <input
                    type="file"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                    id="images"
                    accept="image/*"
                  />
                  <label
                    htmlFor="images"
                    className="flex flex-col items-center justify-center cursor-pointer"
                  >
                    <Upload size={24} className="text-gray-500 mb-2" />
                    <span className="text-sm text-gray-500">
                      Click to upload multiple images
                    </span>
                  </label>
                  {/* Tooltip */}
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
                    <div className="bg-gray-800 text-white text-xs rounded-md py-2 px-3 whitespace-nowrap shadow-lg">
                      Only the image with 1600*900 resolution can be added
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Display selected images */}
              {formData.images.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Selected Images ({formData.images.length})
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {formData.images.map((file, index) => (
                      <div key={index} className="relative">
                        <div className={`h-24 border rounded-md flex items-center justify-center bg-gray-100 overflow-hidden ${index === formData.primaryImageIndex ? 'ring-2 ring-blue-500' : ''
                          }`}>
                          {formData.imagePreviews &&
                            formData.imagePreviews[index] ? (
                            <img
                              src={formData.imagePreviews[index]}
                              alt={file.name || `Image ${index + 1}`}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <p className="text-xs text-gray-500 p-2 truncate">
                              {file.name || "Image " + (index + 1)}
                            </p>
                          )}
                        </div>

                        {/* Primary Image Star */}
                        {index === formData.primaryImageIndex && (
                          <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full p-1">
                            <Star size={12} fill="white" />
                          </div>
                        )}

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                        >
                          <Trash2 size={12} />
                        </button>

                        {/* Set Primary Button */}
                        {index !== formData.primaryImageIndex && (
                          <button
                            type="button"
                            onClick={() => setPrimaryImage(index)}
                            className="absolute bottom-1 left-1 bg-green-500 text-white rounded-full p-1 hover:bg-green-600 transition-colors"
                            title="Set as Primary Image"
                          >
                            <Star size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Primary Image Info */}
                  {formData.images.length > 1 && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-xs text-blue-700">
                        <Star size={12} className="inline mr-1" />
                        Primary image selected: {formData.images[formData.primaryImageIndex]?.name || `Image ${formData.primaryImageIndex + 1}`}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-2 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setIsEditing(false);
                    setEditId(null);
                  }}
                  className="px-4 py-2 bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 bg-blue-500 text-white rounded-md flex items-center ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                    }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : isEditing ? (
                    "Update"
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NEW: Similar Desing */}
      {isSimilarDesngModalOpen && (
        <SimilarDesignModal
          isOpen={isSimilarDesngModalOpen}
          onClose={closeSimilarDesngModal}
          designId={isSimilarDesngModalOpen}
        />
      )}

      {/* Add Popular Design Modal */}
      {showPopularDesignModal && (
        <AddPopularDesign
          isOpen={showPopularDesignModal}
          onClose={() => setShowPopularDesignModal(false)}
        />
      )}
    </div>
  );
};

export default HouseDesign;
